import React from 'react';
import { LoadingBlock } from '../../../../shared/ui/composites';
import { achievementColor } from '../../../../shared/theme/chartPalette';
import { formatReportValue } from '../../utils/reportFormat';
import { evaluateFormulaSafe } from '../../utils/evalFormula';
import { cn } from '../../../../lib/utils';

export const GAUGE_VARIANTS = [
  { value: 'arc', label: 'نیم‌دایره', icon: '◐' },
  { value: 'ring', label: 'حلقه', icon: '○' },
  { value: 'linear', label: 'نوار', icon: '▬' },
  { value: 'bullet', label: 'بولت', icon: '▮' },
  { value: 'steps', label: 'پله‌ای', icon: '▤' },
];

function readValue(row, key, formula, fallback = 0) {
  if (formula && String(formula).trim()) {
    return evaluateFormulaSafe(formula, row || {}, fallback);
  }
  if (key && row && row[key] != null && row[key] !== '') {
    const n = Number(row[key]);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function clampPct(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function ArcGauge({ pct, color, size = 180, label, sub, valueText }) {
  const r = 56;
  const c = 2 * Math.PI * r;
  const dash = (clampPct(pct) / 100) * c * 0.75;
  const gap = c - dash;
  const gradId = `gauge-arc-${Math.round((Number(pct) || 0) * 10)}-${size}`;
  return (
    <div className="flex flex-col items-center justify-center" style={{ width: size, height: size }}>
      <svg viewBox="0 0 140 120" className="w-full">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity="0.7" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        <g transform="rotate(135 70 70)">
          <circle cx="70" cy="70" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" strokeDasharray={`${c * 0.75} ${c}`} strokeLinecap="round" />
          <circle cx="70" cy="70" r={r} fill="none" stroke={`url(#${gradId})`} strokeWidth="12" strokeDasharray={`${dash} ${gap + c * 0.25}`} strokeLinecap="round" />
        </g>
      </svg>
      <div className="-mt-16 flex flex-col items-center">
        <span className="text-3xl font-black tabular-nums" style={{ color }}>
          {valueText || `${Math.round(pct)}%`}
        </span>
        {label ? <span className="mt-1 text-xs font-semibold text-slate-500">{label}</span> : null}
        {sub ? <span className="text-[11px] text-slate-400">{sub}</span> : null}
      </div>
    </div>
  );
}

function RingGauge({ pct, color, size = 160, label, sub, valueText }) {
  const r = 54;
  const c = 2 * Math.PI * r;
  const dash = (clampPct(pct) / 100) * c;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#e2e8f0" strokeWidth="14" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="14" strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black tabular-nums" style={{ color }}>
          {valueText || `${Math.round(pct)}%`}
        </span>
        {label ? <span className="text-[11px] font-semibold text-slate-500">{label}</span> : null}
        {sub ? <span className="text-[10px] text-slate-400">{sub}</span> : null}
      </div>
    </div>
  );
}

function LinearBar({ pct, color, label, sub, valueText }) {
  return (
    <div className="flex h-full w-full flex-col justify-center gap-2 px-1">
      <div className="flex items-end justify-between gap-2">
        <div>
          {label ? <p className="text-xs font-semibold text-slate-500">{label}</p> : null}
          {sub ? <p className="text-[11px] text-slate-400">{sub}</p> : null}
        </div>
        <span className="text-xl font-black tabular-nums" style={{ color }}>{valueText || `${Math.round(pct)}%`}</span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full transition-all" style={{ width: `${clampPct(pct)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function BulletGauge({ pct, color, label, sub, valueText }) {
  const n = clampPct(pct);
  return (
    <div className="flex h-full w-full flex-col justify-center gap-2 px-1">
      <div className="flex items-end justify-between gap-2">
        <div>
          {label ? <p className="text-xs font-semibold text-slate-500">{label}</p> : null}
          {sub ? <p className="text-[11px] text-slate-400">{sub}</p> : null}
        </div>
        <span className="text-lg font-black tabular-nums" style={{ color }}>{valueText || `${Math.round(n)}%`}</span>
      </div>
      <div className="relative h-7 w-full overflow-hidden rounded-md bg-slate-100">
        <div className="absolute inset-y-1 start-0 rounded-sm" style={{ width: `${n}%`, backgroundColor: color }} />
        <div className="absolute inset-y-0 w-0.5 bg-slate-800/80" style={{ insetInlineEnd: '0' }} />
        {[25, 50, 75].map((tick) => (
          <div
            key={tick}
            className="absolute top-0 bottom-0 w-px bg-white/70"
            style={{ insetInlineStart: `${tick}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function StepsGauge({ pct, color, label, sub, valueText, steps = 10 }) {
  const n = clampPct(pct);
  const filled = Math.round((n / 100) * steps);
  return (
    <div className="flex h-full w-full flex-col justify-center gap-2 px-1">
      <div className="flex items-end justify-between gap-2">
        <div>
          {label ? <p className="text-xs font-semibold text-slate-500">{label}</p> : null}
          {sub ? <p className="text-[11px] text-slate-400">{sub}</p> : null}
        </div>
        <span className="text-lg font-black tabular-nums" style={{ color }}>{valueText || `${Math.round(n)}%`}</span>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: steps }).map((_, i) => (
          <div
            key={i}
            className="h-3.5 flex-1 rounded-sm"
            style={{ backgroundColor: i < filled ? color : '#e2e8f0' }}
          />
        ))}
      </div>
    </div>
  );
}

function resolveGauge(widget, data) {
  const cfg = widget.progressConfig || widget.gaugeConfig || {};
  const row = data?.rows?.[0] || {};
  const achieved = readValue(row, cfg.actualKey || cfg.valueKey, cfg.actualFormula || cfg.valueFormula, 0);
  const hasTargetField = !!(cfg.targetKey || cfg.maxKey || cfg.targetFormula || cfg.maxFormula);
  const targetRaw = hasTargetField
    ? readValue(row, cfg.targetKey || cfg.maxKey, cfg.targetFormula || cfg.maxFormula, 0)
    : 0;
  const hasGoal = hasTargetField && Number(targetRaw) > 0;
  const pctOverride = cfg.progressFormula
    ? readValue(row, '', cfg.progressFormula, null)
    : null;
  const pct = pctOverride != null
    ? Number(pctOverride)
    : (hasGoal ? (achieved / targetRaw) * 100 : null);
  const fillPct = hasGoal && pct != null && Number.isFinite(pct) ? clampPct(pct) : 0;
  const color = cfg.color || achievementColor(pct == null ? 0 : pct);
  const valueText = formatReportValue(achieved, {
    format: cfg.format || 'number',
    decimals: cfg.decimals ?? 0,
    useGrouping: true,
  });
  const targetText = hasGoal ? formatReportValue(targetRaw, {
    format: cfg.format || 'number',
    decimals: cfg.decimals ?? 0,
    useGrouping: true,
  }) : '';
  const sub = cfg.subLabel
    || (hasGoal && cfg.showTarget !== false ? `هدف ${targetText}` : '');
  const pctLabel = pct == null || !Number.isFinite(pct) ? '' : `${Math.round(pct)}%`;
  const label = cfg.showValue === false ? '' : (cfg.format === 'percent' ? pctLabel : valueText);
  return { cfg, pct, fillPct, hasGoal, color, valueText, label, sub };
}

function GaugeBody({ variant, pct, color, size, label, sub, valueText, title }) {
  if (variant === 'ring') {
    return <RingGauge pct={pct} color={color} size={size} label={label} sub={sub} valueText={valueText} />;
  }
  if (variant === 'linear') {
    return <LinearBar pct={pct} color={color} label={title} sub={sub} valueText={valueText || label} />;
  }
  if (variant === 'bullet') {
    return <BulletGauge pct={pct} color={color} label={title} sub={sub} valueText={valueText || label} />;
  }
  if (variant === 'steps') {
    return <StepsGauge pct={pct} color={color} label={title} sub={sub} valueText={valueText || label} />;
  }
  return <ArcGauge pct={pct} color={color} size={size} label={label} sub={sub} valueText={valueText} />;
}

export default function GaugeWidget({ widget, data, loading, error }) {
  if (loading) return <LoadingBlock />;
  if (error) return <div className="text-sm text-red-500 p-2">{error}</div>;

  const { cfg, pct, fillPct, hasGoal, color, valueText, label, sub } = resolveGauge(widget, data);
  const size = Number(cfg.size) || 200;
  const variant = cfg.variant || 'arc';
  const mainText = hasGoal && pct != null && Number.isFinite(pct)
    ? `${Math.round(pct)}%`
    : valueText;

  return (
    <div className={cn('flex h-full w-full items-center justify-center')}>
      <GaugeBody
        variant={variant}
        pct={hasGoal ? fillPct : 0}
        color={color}
        size={size}
        label={cfg.format === 'percent' ? '' : label}
        valueText={cfg.showValue === false ? '' : mainText}
        sub={sub}
        title={widget.title}
      />
    </div>
  );
}

export function ProgressBarWidget({ widget, data, loading, error }) {
  if (loading) return <LoadingBlock />;
  if (error) return <div className="text-sm text-red-500 p-2">{error}</div>;
  const { cfg, pct, fillPct, hasGoal, color, valueText, label, sub } = resolveGauge(widget, data);
  const variant = cfg.variant || 'linear';
  const text = hasGoal && pct != null && Number.isFinite(pct)
    ? `${Math.round(pct)}%`
    : (label || valueText);
  return (
    <GaugeBody
      variant={variant === 'arc' || variant === 'ring' ? 'linear' : variant}
      pct={hasGoal ? fillPct : 0}
      color={color}
      size={160}
      label={text}
      valueText={text}
      sub={sub}
      title={widget.title}
    />
  );
}
